import React, { useState, useRef, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Upload, Send, X, Image, Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteForm = () => {
  const { isDark } = useTheme();
  const { toast } = useToast();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    sign_type: '',
    company: '',
    message: ''
  });
  
  // Support multiple files
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Allowed file types for upload
  const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  const allowedDocTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const allowedTypes = [...allowedImageTypes, ...allowedDocTypes];
  
  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType === 'application/pdf') return '📄';
    if (fileType.includes('word')) return '📝';
    return '📎';
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    // Limit total files to 10
    if (uploadedFiles.length + files.length > 10) {
      toast({
        title: 'Too many files',
        description: 'Maximum 10 files allowed per quote request',
        variant: 'destructive'
      });
      return;
    }
    
    setIsUploading(true);
    const newFiles = [];
    
    for (const file of files) {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: `${file.name}: Only images (PNG, JPG, SVG, GIF) and documents (PDF, DOC, DOCX) are allowed`,
          variant: 'destructive'
        });
        continue;
      }
      
      // Validate file size (10MB max per file)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: `${file.name}: Maximum file size is 10MB`,
          variant: 'destructive'
        });
        continue;
      }
      
      // Create preview for images
      let preview = null;
      if (file.type.startsWith('image/')) {
        preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
      }
      
      // Upload to server
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      try {
        const response = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          body: uploadFormData
        });
        
        if (response.ok) {
          const data = await response.json();
          newFiles.push({
            id: Date.now() + Math.random(),
            name: file.name,
            type: file.type,
            url: data.url,
            preview: preview
          });
        } else {
          // If upload fails, still keep the file with preview
          newFiles.push({
            id: Date.now() + Math.random(),
            name: file.name,
            type: file.type,
            url: preview, // Use preview as fallback for images
            preview: preview
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
        if (preview) {
          newFiles.push({
            id: Date.now() + Math.random(),
            name: file.name,
            type: file.type,
            url: preview,
            preview: preview
          });
        }
      }
    }
    
    if (newFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...newFiles]);
      toast({
        title: 'Files uploaded',
        description: `${newFiles.length} file(s) uploaded successfully`
      });
    }
    
    setIsUploading(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileUpload(files);
  }, [uploadedFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) handleFileUpload(files);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Collect all file URLs
      const imageUrls = uploadedFiles.map(f => ({
        url: f.url,
        name: f.name,
        type: f.type
      }));
      
      const quoteData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        sign_type: formData.sign_type || 'Not specified',
        message: formData.message,
        // Keep image_url for backward compatibility (first image)
        image_url: imageUrls.length > 0 ? imageUrls[0].url : null,
        // New field for multiple files
        attachments: imageUrls
      };
      
      const response = await fetch(`${API_URL}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(quoteData)
      });
      
      if (response.ok) {
        toast({
          title: t('quoteForm.success'),
          description: t('quoteForm.successDesc'),
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          sign_type: '',
          company: '',
          message: ''
        });
        setUploadedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit quote');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit quote. Please try again.',
        variant: 'destructive'
      });
    }
    
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div 
      id="quote" 
      className="py-20 px-4 transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 
            className="text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300"
            style={{ 
              color: isDark ? '#00ff88' : '#10b981',
              textShadow: isDark ? '0 0 30px rgba(0, 255, 136, 0.8), 0 0 50px rgba(0, 255, 136, 0.5)' : '0 0 25px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.4)'
            }}
          >
            {t('quoteForm.title')}
          </h2>
          <p 
            className="text-lg transition-colors duration-300"
            style={{ 
              color: isDark ? '#ff00ff' : '#d946ef',
              textShadow: isDark ? '0 0 10px rgba(255, 0, 255, 0.6)' : '0 0 10px rgba(217, 70, 239, 0.5)'
            }}
          >
            {t('quoteForm.subtitle')}
          </p>
        </div>

        <Card 
          className="p-8 transition-colors duration-300"
          style={{
            backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
            borderColor: isDark ? '#00ffff' : '#0ea5e9',
            borderWidth: '2px',
            boxShadow: isDark ? '0 0 40px rgba(0, 255, 255, 0.3)' : '0 0 35px rgba(14, 165, 233, 0.4)'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label 
                  className="mb-2 block transition-colors duration-300"
                  style={{ 
                    color: isDark ? '#ff00ff' : '#d946ef',
                    textShadow: isDark ? '0 0 8px rgba(255, 0, 255, 0.5)' : '0 0 6px rgba(217, 70, 239, 0.4)'
                  }}
                >
                  {t('quoteForm.name')} *
                </Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('quoteForm.namePlaceholder')}
                  className="transition-colors duration-300"
                  style={{
                    backgroundColor: isDark ? '#000' : '#f8fafc',
                    borderColor: isDark ? '#ff00ff' : '#d946ef',
                    borderWidth: '2px',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
              <div>
                <Label 
                  className="mb-2 block transition-colors duration-300"
                  style={{ 
                    color: isDark ? '#ff00ff' : '#d946ef',
                    textShadow: isDark ? '0 0 8px rgba(255, 0, 255, 0.5)' : '0 0 6px rgba(217, 70, 239, 0.4)'
                  }}
                >
                  {t('quoteForm.email')} *
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('quoteForm.emailPlaceholder')}
                  className="transition-colors duration-300"
                  style={{
                    backgroundColor: isDark ? '#000' : '#f8fafc',
                    borderColor: isDark ? '#ff00ff' : '#d946ef',
                    borderWidth: '2px',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label 
                  className="mb-2 block transition-colors duration-300"
                  style={{ 
                    color: isDark ? '#00ffff' : '#0ea5e9',
                    textShadow: isDark ? '0 0 8px rgba(0, 255, 255, 0.5)' : '0 0 6px rgba(14, 165, 233, 0.4)'
                  }}
                >
                  {t('quoteForm.phone')}
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('quoteForm.phonePlaceholder')}
                  className="transition-colors duration-300"
                  style={{
                    backgroundColor: isDark ? '#000' : '#f8fafc',
                    borderColor: isDark ? '#00ffff' : '#0ea5e9',
                    borderWidth: '2px',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                />
              </div>
              <div>
                <Label 
                  className="mb-2 block transition-colors duration-300"
                  style={{ 
                    color: isDark ? '#00ffff' : '#0ea5e9',
                    textShadow: isDark ? '0 0 8px rgba(0, 255, 255, 0.5)' : '0 0 6px rgba(14, 165, 233, 0.4)'
                  }}
                >
                  {t('quoteForm.signType')}
                </Label>
                <select
                  name="sign_type"
                  value={formData.sign_type}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md transition-colors duration-300"
                  style={{
                    backgroundColor: isDark ? '#000' : '#f8fafc',
                    borderColor: isDark ? '#00ffff' : '#0ea5e9',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                >
                  <option value="">{t('quoteForm.selectType')}</option>
                  <option value="LED Neon Signs">LED Neon Signs</option>
                  <option value="LED Signs">LED Signs</option>
                  <option value="Channel Letter Signs">Channel Letter Signs</option>
                  <option value="Acrylic Signs">Acrylic Signs</option>
                  <option value="Metal Signs">Metal Signs</option>
                  <option value="Lightbox Signs">Lightbox Signs</option>
                  <option value="Custom Signs">Custom Signs</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <Label 
                className="mb-2 block transition-colors duration-300"
                style={{ 
                  color: isDark ? '#00ff88' : '#10b981',
                  textShadow: isDark ? '0 0 8px rgba(0, 255, 136, 0.5)' : '0 0 6px rgba(16, 185, 129, 0.4)'
                }}
              >
                {t('quoteForm.message')} *
              </Label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder={t('quoteForm.messagePlaceholder')}
                className="min-h-32 transition-colors duration-300"
                style={{
                  backgroundColor: isDark ? '#000' : '#f8fafc',
                  borderColor: isDark ? '#00ff88' : '#10b981',
                  borderWidth: '2px',
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
              />
            </div>

            {/* File Upload Section */}
            <div>
              <Label 
                className="mb-2 block transition-colors duration-300"
                style={{ 
                  color: isDark ? '#ffffff' : '#64748b'
                }}
              >
                Upload Files (Optional) - Images & Documents
              </Label>
              
              {/* Uploaded Files Preview */}
              {uploadedFiles.length > 0 && (
                <div 
                  className="mb-4 p-4 rounded-lg"
                  style={{
                    backgroundColor: isDark ? 'rgba(0,255,136,0.1)' : 'rgba(16,185,129,0.1)',
                    border: `2px solid ${isDark ? '#00ff88' : '#10b981'}`
                  }}
                >
                  <p style={{ color: isDark ? '#00ff88' : '#10b981' }} className="font-medium mb-3">
                    {uploadedFiles.length} file(s) attached
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {uploadedFiles.map((file) => (
                      <div 
                        key={file.id}
                        className="relative group rounded-lg overflow-hidden"
                        style={{
                          backgroundColor: isDark ? '#1a1a1a' : '#fff',
                          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
                        }}
                      >
                        {file.preview ? (
                          <img 
                            src={file.preview} 
                            alt={file.name}
                            className="w-full h-20 object-cover"
                          />
                        ) : (
                          <div 
                            className="w-full h-20 flex items-center justify-center text-3xl"
                            style={{ backgroundColor: isDark ? '#2a2a2a' : '#f1f5f9' }}
                          >
                            {getFileIcon(file.type)}
                          </div>
                        )}
                        <div className="p-2">
                          <p 
                            className="text-xs truncate"
                            style={{ color: isDark ? '#888' : '#64748b' }}
                            title={file.name}
                          >
                            {file.name}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(file.id)}
                          className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Drop Zone */}
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${isDragging ? 'scale-105' : ''}`}
                style={{
                  borderColor: isDragging 
                    ? (isDark ? '#00ff88' : '#10b981')
                    : (isDark ? '#666' : '#cbd5e1'),
                  backgroundColor: isDragging 
                    ? (isDark ? 'rgba(0,255,136,0.1)' : 'rgba(16,185,129,0.1)')
                    : 'transparent'
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-12 h-12 mx-auto mb-3 animate-spin" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                    <p style={{ color: isDark ? '#00ff88' : '#10b981' }} className="mb-2">Uploading...</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto mb-3" style={{ color: isDark ? '#888' : '#94a3b8' }} />
                    <p style={{ color: isDark ? '#888' : '#64748b' }} className="mb-2">
                      {isDragging ? 'Drop your files here!' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-sm" style={{ color: isDark ? '#666' : '#94a3b8' }}>
                      Images (PNG, JPG, SVG, GIF) & Documents (PDF, DOC, DOCX) up to 10MB each
                    </p>
                    <p className="text-xs mt-1" style={{ color: isDark ? '#555' : '#a1a1aa' }}>
                      Maximum 10 files
                    </p>
                  </>
                )}
                <input 
                  ref={fileInputRef}
                  type="file" 
                  className="hidden" 
                  accept="image/*,.pdf,.doc,.docx"
                  multiple
                  onChange={handleFileSelect}
                />
              </div>
            </div>

            <Button 
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full text-white font-bold"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, #00ff00 0%, #00ff88 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
                boxShadow: isDark 
                  ? '0 10px 50px rgba(0, 255, 136, 0.6), 0 0 30px rgba(0, 255, 0, 0.4)'
                  : '0 10px 45px rgba(16, 185, 129, 0.6), 0 0 30px rgba(20, 184, 166, 0.5)',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('quoteForm.submitting') || 'Submitting...'}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {t('quoteForm.submit')}
                </>
              )}
            </Button>
          </form>
        </Card>

        <div className="mt-8 text-center">
          <p 
            className="text-sm transition-colors duration-300"
            style={{ color: isDark ? '#888' : '#64748b' }}
          >
            Questions? Email us at <a 
              href="mailto:okisignstudio@gmail.com" 
              style={{ 
                color: isDark ? '#00ffff' : '#0ea5e9',
                textShadow: isDark ? '0 0 8px rgba(0, 255, 255, 0.5)' : '0 0 6px rgba(14, 165, 233, 0.4)'
              }}
            >
              okisignstudio@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuoteForm;
